import { useState } from 'react';
import { ArrowLeft, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import EnhancedAppHeader from '@/components/EnhancedAppHeader';

export default function MySubscription() {
  const navigate = useNavigate();
  
  // Mock data - kept for future use
  // const currentPlan = { ... };
  // const bills = [ ... ];

  const weddingPackages = [
    {
      weddings: 1,
      price: 999,
      pricePerWedding: 999,
      popular: false
    },
    {
      weddings: 2,
      price: 1799,
      pricePerWedding: 899,
      popular: true
    },
    {
      weddings: 5,
      price: 3999,
      pricePerWedding: 799,
      popular: false
    },
    {
      weddings: 10,
      price: 6999,
      pricePerWedding: 699,
      popular: false
    }
  ];

  const handleUpgrade = (planName) => {
    toast.info(`Upgrade to ${planName} - Coming soon!`);
    // TODO: Integrate with payment gateway
  };

  const handleDownloadBill = (billId) => {
    toast.success(`Downloading invoice #${billId}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <EnhancedAppHeader />
      
      <div className="container mx-auto p-4 sm:p-6 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text">My Subscription</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your plan and billing</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Wedding Packages */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Wedding Packages
            </h2>

            {/* Pricing Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-border">
                    <th className="text-left p-4 font-semibold">Pack</th>
                    <th className="text-right p-4 font-semibold">Price</th>
                    <th className="text-right p-4 font-semibold">Effective / Wedding</th>
                    <th className="text-center p-4 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {weddingPackages.map((pkg, index) => (
                    <tr
                      key={index}
                      className={`border-b border-border hover:bg-muted/30 transition-colors ${
                        pkg.popular ? 'bg-primary/5' : ''
                      }`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{pkg.weddings} Wedding{pkg.weddings > 1 ? 's' : ''}</span>
                          {pkg.popular && (
                            <Badge className="bg-primary text-primary-foreground text-xs">
                              Best Value
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <span className="text-lg font-bold gradient-text">₹{pkg.price.toLocaleString('en-IN')}</span>
                      </td>
                      <td className="p-4 text-right">
                        <span className="text-base font-semibold">₹{pkg.pricePerWedding.toLocaleString('en-IN')}</span>
                      </td>
                      <td className="p-4 text-center">
                        <Button
                          size="sm"
                          className={pkg.popular ? 'gradient-primary' : ''}
                          variant={pkg.popular ? 'default' : 'outline'}
                          onClick={() => handleUpgrade(`${pkg.weddings} Wedding${pkg.weddings > 1 ? 's' : ''} Pack`)}
                        >
                          Select
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Package Cards (Mobile View) */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 md:hidden">
              {weddingPackages.map((pkg, index) => (
                <div
                  key={index}
                  className={`relative rounded-lg border-2 p-4 transition-all ${
                    pkg.popular ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  {pkg.popular && (
                    <Badge className="absolute -top-2 right-2 gradient-primary text-xs">
                      Best Value
                    </Badge>
                  )}

                  <div className="text-center mb-4">
                    <h3 className="text-lg font-bold mb-1">
                      {pkg.weddings} Wedding{pkg.weddings > 1 ? 's' : ''}
                    </h3>
                    <div className="space-y-1">
                      <div>
                        <span className="text-2xl font-bold gradient-text">₹{pkg.price.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        ₹{pkg.pricePerWedding.toLocaleString('en-IN')} / wedding
                      </div>
                    </div>
                  </div>

                  <Button
                    size="sm"
                    className={`w-full ${
                      pkg.popular ? 'gradient-primary' : ''
                    }`}
                    variant={pkg.popular ? 'default' : 'outline'}
                    onClick={() => handleUpgrade(`${pkg.weddings} Wedding${pkg.weddings > 1 ? 's' : ''} Pack`)}
                  >
                    Select
                  </Button>
                </div>
              ))}
            </div>
          </Card>

          {/* Help Text */}
          <Card className="p-6 bg-muted/30">
            <p className="text-sm text-muted-foreground text-center">
              <strong>Note:</strong> Subscriptions cannot be cancelled. For billing questions, please{' '}
              <button className="text-primary hover:underline" onClick={() => navigate('/help')}>
                contact support
              </button>
              .
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
